import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

// Error messages in all 8 languages
const ERROR_CONTENT = {
  it: {
    title: 'Qualcosa è andato storto',
    message: 'Si è verificato un errore imprevisto. Ci scusiamo per il disagio.',
    retry: 'Riprova',
    home: 'Torna alla Home',
    details: 'Dettagli tecnici'
  },
  en: {
    title: 'Something went wrong',
    message: 'An unexpected error occurred. We apologize for the inconvenience.',
    retry: 'Try again',
    home: 'Back to Home',
    details: 'Technical details'
  },
  de: {
    title: 'Etwas ist schief gelaufen',
    message: 'Ein unerwarteter Fehler ist aufgetreten. Wir entschuldigen uns für die Unannehmlichkeiten.',
    retry: 'Erneut versuchen',
    home: 'Zurück zur Startseite',
    details: 'Technische Details'
  },
  fr: {
    title: 'Une erreur s\'est produite',
    message: 'Une erreur inattendue s\'est produite. Nous nous excusons pour la gêne occasionnée.',
    retry: 'Réessayer',
    home: 'Retour à l\'Accueil',
    details: 'Détails techniques'
  },
  es: {
    title: 'Algo salió mal',
    message: 'Ocurrió un error inesperado. Nos disculpamos por las molestias.',
    retry: 'Reintentar',
    home: 'Volver al Inicio',
    details: 'Detalles técnicos'
  },
  pt: {
    title: 'Algo deu errado',
    message: 'Ocorreu um erro inesperado. Pedimos desculpas pelo inconveniente.',
    retry: 'Tentar novamente',
    home: 'Voltar ao Início',
    details: 'Detalhes técnicos'
  },
  ro: {
    title: 'Ceva nu a mers bine',
    message: 'A apărut o eroare neașteptată. Ne cerem scuze pentru neplăceri.',
    retry: 'Încearcă din nou',
    home: 'Înapoi la Pagina Principală',
    details: 'Detalii tehnice'
  },
  pl: {
    title: 'Coś poszło nie tak',
    message: 'Wystąpił nieoczekiwany błąd. Przepraszamy za niedogodności.',
    retry: 'Spróbuj ponownie',
    home: 'Powrót do Strony Głównej',
    details: 'Szczegóły techniczne'
  }
};

// Get browser language or default to 'en'
const getBrowserLocale = () => {
  const lang = navigator.language?.split('-')[0] || 'en';
  return ERROR_CONTENT[lang] ? lang : 'en';
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Log error to analytics if available
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false
      });
    }

    // Log to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleHome = () => {
    window.location.href = '/';
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      const locale = getBrowserLocale();
      const content = ERROR_CONTENT[locale];

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-16">
          <div className="text-center max-w-md mx-auto">
            {/* Error Icon */}
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full">
                <AlertTriangle className="h-10 w-10 text-red-600" />
              </div>
            </div>

            {/* Error Message */}
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              {content.title}
            </h1>
            <p className="text-gray-600 mb-8">
              {content.message}
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                <RefreshCw className="h-5 w-5" />
                {content.retry}
              </button>
              <button
                onClick={this.handleHome}
                className="inline-flex items-center justify-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-medium transition-colors"
              >
                <Home className="h-5 w-5" />
                {content.home}
              </button>
            </div>

            {/* Technical Details (collapsible) */}
            {this.state.error && (
              <div className="text-left">
                <button
                  onClick={this.toggleDetails}
                  className="text-sm text-gray-500 hover:text-gray-700 underline mb-2"
                >
                  {content.details} {this.state.showDetails ? '▲' : '▼'}
                </button>
                {this.state.showDetails && (
                  <pre className="mt-2 p-4 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-auto max-h-48">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
